import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  CalendarClock,
  Mic2,
  ArrowRight,
  Send,
  Sparkles,
  Rocket,
  Loader2,
  AlertTriangle,
  TrendingUp,
} from "lucide-react";
import { api } from "@/lib/api";
import { readApiError } from "@/lib/format";

function StatTile({ label, value, sub, icon: Icon, testid, hint, delta, positive }) {
  return (
    <div
      data-testid={testid}
      className="admin-card flex min-h-[180px] flex-col justify-between p-6"
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="admin-brass-topstrip" />
          <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-[#C9BEA8]">
            {label}
          </p>
          <p className="admin-metric-value mt-3">{value ?? "—"}</p>
        </div>
        {Icon && (
          <span className="rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/12 p-2.5 text-[#C9A961]">
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
      <div className="mt-3 space-y-1">
        {delta != null && (
          <p
            className={`inline-flex items-center gap-1 text-xs font-medium ${
              positive === false ? "text-[#B85A4A]" : "text-[#7A8B6F]"
            }`}
          >
            <TrendingUp className={`h-3.5 w-3.5 ${positive === false ? "rotate-180" : ""}`} />
            {delta}
          </p>
        )}
        {sub && <p className="text-xs text-[#C9BEA8]">{sub}</p>}
        {hint && <p className="text-[11px] text-[#9A8E78]">{hint}</p>}
      </div>
    </div>
  );
}

function QuickAction({ to, title, sub, icon: Icon, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="admin-card group flex items-center justify-between p-5"
    >
      <div className="flex items-center gap-4">
        <span className="rounded-xl border border-[#C9A961]/40 bg-[#C9A961]/12 p-3 text-[#C9A961]">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="font-serif text-lg tracking-wide text-[#F5EFE2]">{title}</p>
          <p className="text-xs text-[#C9BEA8]">{sub}</p>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 text-[#C9A961] transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

export default function AdminDashboard() {
  const [metrics, setMetrics] = useState({
    members: null,
    leads: null,
    posts: null,
    voice: null,
  });
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, l, p, v, d] = await Promise.all([
          api.get("/admin/metrics/members").then((r) => r.data).catch(() => null),
          api.get("/admin/metrics/leads").then((r) => r.data).catch(() => null),
          api.get("/admin/metrics/posts").then((r) => r.data).catch(() => null),
          api.get("/admin/metrics/voice").then((r) => r.data).catch(() => null),
          api.get("/admin/digest/today").then((r) => r.data).catch(() => null),
        ]);
        if (!cancelled) {
          setMetrics({ members: m, leads: l, posts: p, voice: v });
          setDigest(d);
        }
      } catch (e) {
        toast.error("Could not load dashboard", { description: readApiError(e) });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const mrr = metrics.members?.mrr_cents
    ? `$${((metrics.members.mrr_cents || 0) / 100).toLocaleString()} MRR`
    : null;

  return (
    <div className="space-y-10" data-testid="admin-dashboard">
      <div>
        <p className="admin-eyebrow">Owner Command Center</p>
        <h1 className="admin-h1 mt-2">Morning, Kenny.</h1>
        <p className="mt-3 text-sm text-[#C9BEA8]">
          A quick look at the workshop today.
        </p>
      </div>

      {/* Metrics */}
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          testid="tile-members"
          label="Members"
          value={loading ? "…" : (metrics.members?.count ?? 0).toLocaleString()}
          sub={mrr}
          icon={Users}
          hint={metrics.members?.note}
        />
        <StatTile
          testid="tile-leads"
          label="Leads"
          value={loading ? "…" : (metrics.leads?.total ?? 0).toLocaleString()}
          delta={
            metrics.leads?.last_24h != null
              ? `+${metrics.leads.last_24h} last 24h`
              : null
          }
          positive={(metrics.leads?.last_24h ?? 0) > 0}
          sub={
            metrics.leads?.last_7d != null ? `${metrics.leads.last_7d} in the last 7 days` : null
          }
          icon={UserPlus}
        />
        <StatTile
          testid="tile-posts"
          label="Scheduled"
          value={loading ? "…" : (metrics.posts?.pending ?? 0).toLocaleString()}
          delta={
            metrics.posts?.posted_last_7d != null
              ? `${metrics.posts.posted_last_7d} posted · 7d`
              : null
          }
          sub={
            metrics.posts?.failed_last_7d != null
              ? `${metrics.posts.failed_last_7d} failed · 7d`
              : null
          }
          positive={(metrics.posts?.failed_last_7d ?? 0) === 0}
          icon={CalendarClock}
        />
        <StatTile
          testid="tile-voice"
          label="Voice clips"
          value={loading ? "…" : (metrics.voice?.total ?? 0).toLocaleString()}
          sub={
            metrics.voice?.total_seconds != null
              ? `${Math.round(metrics.voice.total_seconds)}s of Daniel`
              : null
          }
          icon={Mic2}
        />
      </div>

      <div className="admin-section-divider" aria-hidden="true">
        <span className="dot" />
      </div>

      {/* Digest */}
      <div
        data-testid="digest-tile"
        className="admin-card p-7"
      >
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="admin-eyebrow">Today&apos;s digest</p>
            <h2 className="admin-h2 mt-2">The morning read</h2>
          </div>
          <div className="group relative">
            <button
              type="button"
              disabled
              data-testid="digest-send-btn"
              className="inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-[#6E6656] px-3 py-1.5 text-xs text-[#9A8E78]"
            >
              <Send className="h-3.5 w-3.5" />
              Send to Kenny&apos;s email
            </button>
            <span className="pointer-events-none absolute right-0 top-full mt-1 whitespace-nowrap rounded bg-[#3D3A33] px-2 py-1 text-[10px] text-[#C9BEA8] opacity-0 shadow group-hover:opacity-100">
              Pending Resend domain verification
            </span>
          </div>
        </div>

        {digest ? (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#9A8E78]">New leads · 24h</p>
              <p className="admin-metric-value mt-2" style={{ fontSize: "2rem" }}>
                {digest.new_leads_count ?? 0}
              </p>
              {digest.new_leads?.length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs text-[#C9BEA8]">
                  {digest.new_leads.slice(0, 3).map((l) => (
                    <li key={l.email} className="truncate">{l.email}</li>
                  ))}
                </ul>
              ) : (
                <p className="mt-2 text-xs text-[#9A8E78]">Nothing new since yesterday</p>
              )}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#9A8E78]">New members · 24h</p>
              <p className="admin-metric-value mt-2" style={{ fontSize: "2rem" }}>
                {digest.new_subscribers_count ?? 0}
              </p>
              {digest.new_subscribers?.length === 0 && (
                <p className="mt-2 text-xs text-[#9A8E78]">No new members yet</p>
              )}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#9A8E78]">Going out today</p>
              <p className="admin-metric-value mt-2" style={{ fontSize: "2rem" }}>
                {digest.scheduled_today_count ?? 0}
              </p>
              {digest.scheduled_today?.length === 0 && (
                <p className="mt-2 text-xs text-[#9A8E78]">Empty schedule</p>
              )}
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-[#9A8E78]">Failed · 24h</p>
              <p
                className="admin-metric-value mt-2"
                style={{
                  fontSize: "2rem",
                  color: (digest.failed_last_24h_count ?? 0) > 0 ? "#B85A4A" : "#C9A961",
                }}
              >
                {digest.failed_last_24h_count ?? 0}
              </p>
              {digest.failed_last_24h?.length > 0 && (
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-[#B85A4A]">
                  <AlertTriangle className="h-3 w-3" />
                  needs attention
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="mt-5 flex items-center gap-2 text-sm text-[#9A8E78]">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading digest…
          </div>
        )}
      </div>

      <div className="admin-section-divider" aria-hidden="true">
        <span className="dot" />
      </div>

      {/* Quick actions */}
      <div>
        <div className="mb-5">
          <p className="admin-eyebrow">Quick actions</p>
          <h2 className="admin-h2 mt-2">Get moving</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <QuickAction
            testid="action-composer"
            to="/admin/composer"
            title="Compose a Post"
            sub="AI copy → Daniel voice → Ayrshare schedule, in one click"
            icon={Sparkles}
          />
          <QuickAction
            testid="action-calendar"
            to="/admin/calendar"
            title="Generate Next Two Weeks"
            sub="14 days × 6 platforms of on-brand drafts"
            icon={CalendarClock}
          />
          <QuickAction
            testid="action-launchkit"
            to="/admin/launch-kit"
            title="Render Launch Video Voiceover"
            sub="60-second script + storyboard + Daniel MP3"
            icon={Rocket}
          />
        </div>
      </div>
    </div>
  );
}
